/**
 * Mock data for the MockDataProvider
 * Realistic sample books, chapters, and content
 */

import { nanoid } from 'nanoid'
import type { Book, Chapter, Content } from '@/core/models'

// Sample Book IDs
export const SAMPLE_BOOK_IDS = {
  SCIFI: nanoid(),
  MYSTERY: nanoid(),
  GUIDE: nanoid(),
}

// Sample Chapter IDs
export const SAMPLE_CHAPTER_IDS = {
  SCIFI_1: nanoid(),
  SCIFI_2: nanoid(),
  SCIFI_3: nanoid(),
  MYSTERY_1: nanoid(),
  MYSTERY_2: nanoid(),
  GUIDE_1: nanoid(),
  GUIDE_2: nanoid(),
  GUIDE_3: nanoid(),
}

// Sample Books
export const sampleBooks: Book[] = [
  {
    id: SAMPLE_BOOK_IDS.SCIFI,
    title: 'The Quantum Paradox',
    author: 'Jane Doe',
    description: 'A thrilling science fiction novel about time travel and parallel universes.',
    coverImage: 'https://picsum.photos/seed/scifi/400/600',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-03-10'),
    status: 'draft',
    metadata: {
      language: 'en',
      genre: ['Science Fiction', 'Thriller'],
      tags: ['time travel', 'multiverse', 'quantum physics'],
      wordCount: 45230,
      pageCount: 320,
    },
    chapterIds: [SAMPLE_CHAPTER_IDS.SCIFI_1, SAMPLE_CHAPTER_IDS.SCIFI_2, SAMPLE_CHAPTER_IDS.SCIFI_3],
  },
  {
    id: SAMPLE_BOOK_IDS.MYSTERY,
    title: 'Shadows in the Fog',
    author: 'John Smith',
    description: 'A noir detective mystery set in 1940s San Francisco.',
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-03-15'),
    publishedAt: new Date('2024-03-15'),
    status: 'published',
    metadata: {
      isbn: '978-0-123456-78-9',
      publisher: 'Mystery House Publishing',
      language: 'en',
      genre: ['Mystery', 'Noir', 'Historical Fiction'],
      tags: ['detective', '1940s', 'san francisco'],
      wordCount: 72450,
      pageCount: 480,
    },
    chapterIds: [SAMPLE_CHAPTER_IDS.MYSTERY_1, SAMPLE_CHAPTER_IDS.MYSTERY_2],
  },
  {
    id: SAMPLE_BOOK_IDS.GUIDE,
    title: 'The Complete Guide to TypeScript',
    author: 'Alex Developer',
    description: 'A comprehensive guide to mastering TypeScript for modern web development.',
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-03-12'),
    status: 'draft',
    metadata: {
      language: 'en',
      genre: ['Programming', 'Education', 'Reference'],
      tags: ['typescript', 'javascript', 'web development', 'programming'],
      wordCount: 38920,
      pageCount: 250,
    },
    chapterIds: [SAMPLE_CHAPTER_IDS.GUIDE_1, SAMPLE_CHAPTER_IDS.GUIDE_2, SAMPLE_CHAPTER_IDS.GUIDE_3],
  },
]

// Sample Chapters
export const sampleChapters: Chapter[] = [
  // Quantum Paradox chapters
  {
    id: SAMPLE_CHAPTER_IDS.SCIFI_1,
    bookId: SAMPLE_BOOK_IDS.SCIFI,
    title: 'The Discovery',
    order: 1,
    content: 'Dr. Sarah Chen stared at the quantum computer display, her hands trembling. The readings were impossible—the particle existed in two states simultaneously, not just in theory, but in measurable reality. She checked the instruments again, but the data remained consistent. This was no measurement error. This was proof that parallel universes weren\'t just theoretical constructs.\n\n"Run the simulation again," she whispered to her AI assistant, ARIA. The holographic display flickered to life, showing the quantum entanglement patterns that defied everything she thought she knew about physics. Each simulation produced the same result: consciousness could bridge the gap between realities.\n\nThe implications were staggering.',
    wordCount: 892,
    status: 'completed',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-02-10'),
    notes: 'Establish the scientific discovery that drives the plot',
  },
  {
    id: SAMPLE_CHAPTER_IDS.SCIFI_2,
    bookId: SAMPLE_BOOK_IDS.SCIFI,
    title: 'The First Jump',
    order: 2,
    content: 'Three months of preparation had led to this moment. Sarah stood in the center of the quantum chamber, surrounded by a web of electromagnetic fields. The theory was sound: by manipulating her quantum signature, she could shift her consciousness to a parallel version of herself.\n\n"Initializing quantum field," ARIA announced. The room hummed with energy. Sarah closed her eyes and focused on the coordinates—a universe where she had made different choices, where her sister was still alive.\n\nThe sensation was unlike anything she had experienced. Reality seemed to ripple and fold.',
    wordCount: 1245,
    status: 'draft',
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-03-05'),
  },
  {
    id: SAMPLE_CHAPTER_IDS.SCIFI_3,
    bookId: SAMPLE_BOOK_IDS.SCIFI,
    title: 'The Paradox',
    order: 3,
    content: 'When Sarah opened her eyes, she was still in the lab—but everything was different. The equipment was older, the walls a different color. And there, standing in front of her, was her sister Emma, alive and well.\n\n"Sarah? Are you okay?" Emma asked, concern in her voice.\n\nBut something was wrong. Sarah could feel the quantum instability, like a headache at the edge of consciousness. The two universes were trying to reconcile, and she was the point of conflict.',
    wordCount: 856,
    status: 'draft',
    createdAt: new Date('2024-02-15'),
    updatedAt: new Date('2024-03-08'),
  },

  // Shadows in the Fog chapters
  {
    id: SAMPLE_CHAPTER_IDS.MYSTERY_1,
    bookId: SAMPLE_BOOK_IDS.MYSTERY,
    title: 'A Body in the Bay',
    order: 1,
    content: 'The fog rolled in thick off the San Francisco Bay, just like it did every night. Detective Jack Morrison stood on the pier, his fedora pulled low against the damp chill. At his feet, covered by a tarp, was the reason he\'d been called out at 2 AM.\n\n"What do we got, Murphy?" he asked his partner.\n\n"Jane Doe. No identification. Single gunshot wound to the chest. She\'s been in the water maybe six hours." Murphy flipped open his notebook. "But here\'s the interesting part—she\'s wearing a dress that costs more than my annual salary."\n\nJack knelt beside the body. Even in death, he could see she had been beautiful. Expensive jewelry, manicured nails, but no purse, no ID. Someone had wanted her found, but not identified.',
    wordCount: 2341,
    status: 'completed',
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: SAMPLE_CHAPTER_IDS.MYSTERY_2,
    bookId: SAMPLE_BOOK_IDS.MYSTERY,
    title: 'The Nightclub',
    order: 2,
    content: 'The Blue Parrot was the kind of place where secrets went to hide. Jack pushed through the smoky interior, his eyes adjusting to the dim light. A jazz band played in the corner, their music mixing with the clink of glasses and low conversation.\n\n"Help you, detective?" The voice came from behind the bar—a woman in her forties with sharp eyes that missed nothing.\n\n"Looking for information about a girl," Jack said, sliding a photo across the bar. "Seen her?"\n\nThe bartender\'s expression didn\'t change, but Jack saw the recognition in her eyes. "Lots of girls come through here."\n\n"This one\'s dead."\n\nThat got her attention.',
    wordCount: 1876,
    status: 'completed',
    createdAt: new Date('2024-02-10'),
    updatedAt: new Date('2024-03-10'),
  },

  // TypeScript Guide chapters
  {
    id: SAMPLE_CHAPTER_IDS.GUIDE_1,
    bookId: SAMPLE_BOOK_IDS.GUIDE,
    title: 'Introduction to TypeScript',
    order: 1,
    content: 'TypeScript is a strongly typed programming language that builds on JavaScript, giving you better tooling at any scale. Developed and maintained by Microsoft, TypeScript has become the de facto standard for building large-scale JavaScript applications.\n\n## Why TypeScript?\n\nJavaScript is a dynamically typed language, which means type errors often only appear at runtime. TypeScript adds static type checking, catching errors during development rather than in production.\n\n```typescript\n// JavaScript - error only at runtime\nfunction greet(name) {\n  return "Hello, " + name.toUpperCase()\n}\ngreet(42) // Runtime error!\n\n// TypeScript - error caught during development\nfunction greet(name: string): string {\n  return "Hello, " + name.toUpperCase()\n}\ngreet(42) // Compile error: Argument of type \'number\' is not assignable to parameter of type \'string\'.\n```',
    wordCount: 1523,
    status: 'completed',
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-02-25'),
  },
  {
    id: SAMPLE_CHAPTER_IDS.GUIDE_2,
    bookId: SAMPLE_BOOK_IDS.GUIDE,
    title: 'Type System Fundamentals',
    order: 2,
    content: 'Understanding TypeScript\'s type system is crucial to writing effective TypeScript code. This chapter covers the fundamental types and how to use them.\n\n## Basic Types\n\nTypeScript includes several basic types:\n\n- `string`: Text values\n- `number`: Numeric values (integers and floats)\n- `boolean`: true/false values\n- `array`: Collections of values\n- `tuple`: Fixed-length arrays with known types\n- `enum`: Named constants\n- `any`: Opt-out of type checking\n- `unknown`: Type-safe counterpart of any\n- `void`: Absence of value\n- `null` and `undefined`: Nullable values',
    wordCount: 1342,
    status: 'review',
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: SAMPLE_CHAPTER_IDS.GUIDE_3,
    bookId: SAMPLE_BOOK_IDS.GUIDE,
    title: 'Advanced Types and Patterns',
    order: 3,
    content: 'Once you understand the basics, TypeScript offers powerful advanced features for building robust applications.\n\n## Union Types\n\nUnion types allow a value to be one of several types:\n\n```typescript\ntype Status = "pending" | "success" | "error"\nlet currentStatus: Status = "pending"\n```\n\n## Generics\n\nGenerics provide a way to create reusable components:\n\n```typescript\nfunction identity<T>(arg: T): T {\n  return arg\n}\n```',
    wordCount: 987,
    status: 'draft',
    createdAt: new Date('2024-02-15'),
    updatedAt: new Date('2024-03-05'),
  },
]

// Sample Content
export const sampleContent: Map<string, Content> = new Map([
  [
    SAMPLE_CHAPTER_IDS.SCIFI_1,
    {
      chapterId: SAMPLE_CHAPTER_IDS.SCIFI_1,
      text: sampleChapters[0].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.SCIFI_2,
    {
      chapterId: SAMPLE_CHAPTER_IDS.SCIFI_2,
      text: sampleChapters[1].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.SCIFI_3,
    {
      chapterId: SAMPLE_CHAPTER_IDS.SCIFI_3,
      text: sampleChapters[2].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.MYSTERY_1,
    {
      chapterId: SAMPLE_CHAPTER_IDS.MYSTERY_1,
      text: sampleChapters[3].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.MYSTERY_2,
    {
      chapterId: SAMPLE_CHAPTER_IDS.MYSTERY_2,
      text: sampleChapters[4].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.GUIDE_1,
    {
      chapterId: SAMPLE_CHAPTER_IDS.GUIDE_1,
      text: sampleChapters[5].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.GUIDE_2,
    {
      chapterId: SAMPLE_CHAPTER_IDS.GUIDE_2,
      text: sampleChapters[6].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
  [
    SAMPLE_CHAPTER_IDS.GUIDE_3,
    {
      chapterId: SAMPLE_CHAPTER_IDS.GUIDE_3,
      text: sampleChapters[7].content,
      formatting: [],
      version: 1,
      lastSaved: new Date(),
      autoSaveEnabled: true,
    },
  ],
])
